import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Pager } from '@/shared/ui/pager';

import { LIST_PAGE_SIZES, type ListPageController } from '../model/useListPageController';

const NUMBER_FORMAT = new Intl.NumberFormat('cs-CZ');

/** Skok na stránku posuvníkem — popisek se aktualizuje během tažení, načtení až po puštění. */
function PageSlider<TItem>({ controller }: { controller: ListPageController<TItem> }) {
  const { t } = useTranslation('listPage');
  const [value, setValue] = useState(controller.page);
  useEffect(() => setValue(controller.page), [controller.page]);

  if (controller.totalPages <= 1) {
    return null;
  }

  const commit = (next: number) =>
    controller.setPage(Math.min(controller.totalPages, Math.max(1, next)));

  return (
    <span className="gap-phx-3 text-phx-sm text-phx-text-muted inline-flex items-center whitespace-nowrap">
      <span>
        {t('pager.page')} <b className="text-phx-text tabular-nums">{value}</b> /{' '}
        {controller.totalPages}
      </span>
      <input
        type="range"
        min={1}
        max={controller.totalPages}
        value={value}
        disabled={!controller.searched || controller.isLoading}
        className="phxlist-pageslider"
        onChange={(e) => setValue(Number(e.target.value))}
        onMouseUp={(e) => commit(Number((e.target as HTMLInputElement).value))}
        onTouchEnd={(e) => commit(Number((e.target as HTMLInputElement).value))}
        onKeyUp={(e) => commit(Number((e.target as HTMLInputElement).value))}
      />
    </span>
  );
}

/**
 * Patička stránkování (anatomie 04 §2) — velikost stránky, rozsah, posuvníkový skok
 * a číslovaný pager (sdílený idiom « ‹ čísla › »). Mimo načtený stav disabled.
 */
export function PagerFooter<TItem>({ controller }: { controller: ListPageController<TItem> }) {
  const { t } = useTranslation('listPage');
  const disabled = !controller.searched || controller.isLoading;
  const rangeFrom = (controller.page - 1) * controller.pageSize + 1;
  const rangeTo = (controller.page - 1) * controller.pageSize + controller.items.length;

  return (
    <div className="phxlist-pagination" data-disabled={disabled || undefined}>
      <div className="gap-phx-2 flex items-center">
        <span className="text-phx-sm text-phx-text-muted">{t('pager.show')}</span>
        <span className="phxlist-btngroup">
          {LIST_PAGE_SIZES.map((size) => (
            <button
              key={size}
              type="button"
              className="phxlist-btn"
              data-size="sm"
              data-active={controller.pageSize === size || undefined}
              disabled={disabled}
              onClick={() => controller.setPageSize(size)}
            >
              {size}
            </button>
          ))}
        </span>
        <span className="text-phx-sm text-phx-text-muted ml-phx-2 tabular-nums">
          {controller.searched
            ? controller.totalCount > 0
              ? t('pager.range', {
                  from: NUMBER_FORMAT.format(rangeFrom),
                  to: NUMBER_FORMAT.format(rangeTo),
                  total: NUMBER_FORMAT.format(controller.totalCount),
                })
              : t('pager.empty')
            : '—'}
        </span>
      </div>
      <div className="gap-phx-2 ml-auto flex items-center">
        <PageSlider controller={controller} />
        {controller.totalPages > 1 ? <span className="phxlist-vdivider" /> : null}
        <Pager
          currentPage={controller.page}
          totalPages={controller.totalPages}
          onPageChange={controller.setPage}
          disabled={disabled}
          numbers
          prevNextText={{ previous: t('pager.previous'), next: t('pager.next') }}
          labels={{
            first: t('pager.first'),
            previous: t('pager.previous'),
            next: t('pager.next'),
            last: t('pager.last'),
            page: (page) => t('pager.pageLabel', { page }),
          }}
        />
      </div>
    </div>
  );
}
